/* eslint-disable class-methods-use-this */
import {action, observable, runInAction} from 'mobx';
import {fromCallback} from 'promise-cb';

import {withConfig, withI18n} from 'spa/Components/Context';

import {addPopup, removePopup} from 'spa/lib/popup';

import APIRequestState from '../_util/api-request';

const isServer = typeof window === 'undefined';

const HTTP_HOST = isServer ?
  process.env.HTTP_HOST :
  window.HTTP_HOST;

@withConfig
@withI18n
class CheckoutComState extends APIRequestState {
  @observable canMakeApplePayments = false;
  @observable isValidatingApplePay = false;

  @action('initGateway - checkout-com')
  initGateway(gatewayInfo) {
    let debugMode;

    if (__DEV__) {
      debugMode = true;
    }

    // The ApplePay merchant ID might be in applePayMerchantID or gatewayMerchantID
    this.applePayMerchantID = gatewayInfo.applePayMerchantID || gatewayInfo.gatewayMerchantID;

    if (window && window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
      ApplePaySession.canMakePaymentsWithActiveCard(this.applePayMerchantID)
        .then((response) => {
          this.canMakeApplePayments = response;
        });
    }

    return new Promise(((resolve, reject) => {
      if (window.CheckoutKit) {
        window.CheckoutKit.configure({
          debugMode,
          publicKey: gatewayInfo.clientToken,
        });

        return resolve({
          success: true,
        });
      }

      return reject(new Error());
    }));
  }

  /*
   *  validateApplePay - validate the merchant for ApplePay
   */
  @action('validateApplePay - CheckoutComState')
  async validateApplePay(data, countryId) {
    const {applePay, config} = this.ctx;

    const baseDomain = __DEV__ ? 'localhost:3001' : HTTP_HOST;

    const displayName = config.getValue('locale.apple-pay-display-name', countryId);

    const requestData = {
      country_id: countryId,
      gateway: 'checkout',
      validationData: {
        validationURL: data.validationURL,
        merchantIdentifier: this.applePayMerchantID,
        displayName,
        initiative: 'web',
        initiativeContext: baseDomain,
      },
    };

    return new Promise(((resolve, reject) => {
      applePay.load({}, requestData)
        .then(response => resolve(response.body))
        .catch(error => reject(new Error(error)));
    }));
  }

  /*
   *  getApplePayPaymentData - get the object with the payment data
   */
  @action('validateApplePay - CheckoutComState')
  getApplePayPaymentData(data) {
    const {payment} = data;
    const {token} = payment;
    const {paymentData} = token;

    return new Promise(((resolve, reject) => {
      if (!paymentData) {
        reject();
      }

      resolve({
        chargeTokenData: {
          gateway: 'checkout',
          gatewayTokenizationType: 'apple-pay',
          paymentMethodNonce: 'apple-pay-data-in-extra-data',
          extraGatewayData: paymentData,
        },
        paymentType: 'charge',
        clientSupports3DSecure: true,
        passed3DSecure: true,
      });
    }));
  }

  /*
   *  createPaymentRequest - create the payment request object to initialise ApplePay session
   */
  @action('createPaymentRequest - CheckoutComState')
  createPaymentRequest(data) {
    const {
      supportedNetworks,
      capabilities: merchantCapabilities,
      country_id: countryCode,
      currency_id: currencyCode,
      total,
    } = data;

    return {
      countryCode,
      currencyCode,
      merchantCapabilities,
      supportedNetworks,
      total,
    };
  }

  @action('tokenizeCard - checkout-com')
  tokenizeCard(cardData) {
    window.CheckoutKit.setBillingDetails({
      postCode: cardData.billingAddress.postalCode,
    });

    return fromCallback((cb) => {
      window.CheckoutKit.createCardToken(
        {
          cvv: cardData.cvv,
          expiryMonth: cardData.expirationMonth,
          expiryYear: cardData.expirationYear,
          name: cardData.name,
          number: cardData.number,
        },
        {
          includeBinData: false,
        }, (response, err) => {
          if (response.id) {
            response.nonce = response.id;
          }
          cb(err, response);
        },
      );
    });
  }

  @action async verify3DS(options) {
    const {i18n} = this.ctx;
    const iframe = document.createElement('iframe');
    iframe.className = 'checkout-com__3ds-iframe';
    iframe.src = options.nonce;

    const iframeContainer = addPopup(iframe);

    let receiveMessage;

    if (typeof window !== 'undefined') {
      return new Promise(((resolve, reject) => {
        (
          window.addEventListener('message', receiveMessage = (event) => {
            const {
              chargeId, closePopup, error, errorCode, fromCheckout3DSRedirect, paymentToken, query,
            } = event && event.data;

            if (!fromCheckout3DSRedirect) {
              return null;
            }

            if (closePopup) {
              removePopup(iframeContainer);
              window.removeEventListener('message', receiveMessage);
            }

            if (event.origin !== i18n.baseDomain) {
              return reject(new Error(i18n._x('An error occured, please try again.', '3DS payment check error')));
            }

            if (!error) {
              return resolve({
                nonce: paymentToken,
                query,
              });
            }

            const newError = new Error('3ds failure');
            newError.code = errorCode;
            newError.chargeId = chargeId;

            return reject(newError);
          })
        );
      }));
    }
  }
}

export default CheckoutComState;



// WEBPACK FOOTER //
// ./spa/state/payments/checkout-com.jsx